/* ─── format.ts — Canonical number / money / bytes formatters ──────
 *
 *  Single source of truth for magnitude-abbreviated display.
 *  Import from '@/utils/format' everywhere — no local copies.
 *
 *  Default: 2 decimals.  Pass `decimals` to override per call.
 * ────────────────────────────────────────────────────────────────── */

/** Magnitude tiers for decimal numbers (1 000-based). */
const MAG: [number, string][] = [
  [1e12, 'T'],
  [1e9,  'B'],
  [1e6,  'M'],
  [1e3,  'K'],
]

/**
 * Compact number with magnitude suffix.
 *
 * ```
 * fmtCompact(1_234)       → "1.23K"
 * fmtCompact(4_800_000)   → "4.80M"
 * fmtCompact(42)          → "42.00"
 * fmtCompact(42, 0)       → "42"
 * ```
 */
export function fmtCompact(n: number, decimals = 2): string {
  if (n < 0) return `-${fmtCompact(-n, decimals)}`
  for (const [threshold, suffix] of MAG) {
    if (n >= threshold) return `${(n / threshold).toFixed(decimals)}${suffix}`
  }
  return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n))
}

/**
 * Dollar-prefixed compact number.
 *
 * ```
 * fmtMoneyCompact(29_891.05)   → "$29.89K"
 * fmtMoneyCompact(1_635)       → "$1.64K"
 * fmtMoneyCompact(42.5)        → "$42.50"
 * ```
 */
export function fmtMoneyCompact(n: number, decimals = 2): string {
  return `$${fmtCompact(n, decimals)}`
}

/**
 * Full-precision locale-formatted dollar amount.
 *
 * ```
 * fmtMoney(29_891.05)   → "$29,891.05"
 * ```
 */
export function fmtMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Binary magnitude tiers (1 024-based). */
const BYTES: [number, string][] = [
  [1024 ** 4, 'TB'],
  [1024 ** 3, 'GB'],
  [1024 ** 2, 'MB'],
  [1024,      'KB'],
]

/**
 * Human-readable byte size.
 *
 * ```
 * fmtBytes(2_621_440)   → "2.50 MB"
 * fmtBytes(900)         → "900 B"
 * ```
 */
export function fmtBytes(bytes: number, decimals = 2): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B'
  for (const [threshold, unit] of BYTES) {
    if (bytes >= threshold) return `${(bytes / threshold).toFixed(decimals)} ${unit}`
  }
  return `${Math.round(bytes)} B`
}
